import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";

/**
 * Live social-proof stats strip.
 *
 * Poll interval 30s. Numbers tick up with a subtle count-up on mount.
 */
export default function LiveStats({ variant = "hero", className = "" }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = () =>
      api.get("/stats/public").then((r) => {
        if (!cancelled) setStats(r.data);
      }).catch(() => {});
    fetch();
    const t = setInterval(fetch, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!stats) return null;

  const items = [
    { label: "Verified ladies", value: stats.verified, hot: false },
    { label: "Live right now", value: stats.live_now, hot: stats.live_now > 0 },
    { label: "New this week", value: stats.new_this_week, hot: stats.new_this_week > 0 },
    { label: "Active cities", value: stats.cities_active, hot: false },
  ];

  if (variant === "advertiser") {
    // Show demand-side numbers so advertisers know there's traffic.
    const memberOnly = Math.max(0, (stats.members || 0) - (stats.advertisers || 0));
    return (
      <div data-testid="live-stats-advertiser" className={`grid grid-cols-2 gap-6 md:grid-cols-4 ${className}`}>
        {[
          { label: "Members browsing", value: memberOnly },
          { label: "Cities in play", value: stats.cities_active },
          { label: "New this week", value: stats.new_this_week },
          { label: "Featured spots left", value: stats.featured_spots_remaining, hot: stats.featured_spots_remaining <= 3 },
        ].map((i, idx) => (
          <motion.div
            key={i.label}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.08, duration: 0.6 }}
            className="border-l-2 border-velvet-gold/50 pl-4"
          >
            <p className={`font-heading text-5xl font-light tabular-nums ${i.hot ? "text-velvet-rose" : "text-white"}`}>
              {i.value}
            </p>
            <p className="mt-1 text-[10px] uppercase tracking-luxe text-white/50">{i.label}</p>
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div
      data-testid="live-stats-hero"
      className={`flex flex-wrap items-center gap-x-8 gap-y-4 ${className}`}
    >
      {items.map((i, idx) => (
        <motion.div
          key={i.label}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 + idx * 0.1, duration: 0.6 }}
          className="flex items-center gap-2.5"
        >
          {i.hot ? (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-velvet-rose opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-velvet-rose" />
            </span>
          ) : (
            <span className="h-2 w-2 rounded-full bg-velvet-gold/70" />
          )}
          <span className={`text-sm tabular-nums ${i.hot ? "text-velvet-rose" : "text-white"}`}>
            <span className="font-medium">{i.value}</span>
            <span className="ml-1.5 text-[10px] uppercase tracking-luxe text-white/50">
              {i.label}
            </span>
          </span>
        </motion.div>
      ))}
    </div>
  );
}
